import React from 'react';
import { useTheme } from './ThemeProvider';
import { ThemeDefinition } from './types';
import { Check, Palette, Sun, Moon } from 'lucide-react';


export const ThemeSelector: React.FC = () => {
  const { theme, availableThemes, setTheme } = useTheme();

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="p-1.5 rounded-lg bg-[var(--accent-muted)] border border-[var(--border-default)] text-[var(--accent-primary)]">
            <Palette className="w-4 h-4" />
          </div>
          <div>
            <h3 className="text-sm font-mono font-bold text-[var(--text-primary)]">
              Terminal Appearance
            </h3>
            <p className="text-[11px] font-mono text-[var(--text-muted)]">
              Select a custom trading desk visual theme. Applied instantly and saved locally on your device.
            </p>
          </div>
        </div>
        <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-[var(--bg-surface-elevated)] border border-[var(--border-default)] text-[var(--text-secondary)] hidden sm:inline-block">
          {availableThemes.length} THEMES AVAILABLE
        </span>
      </div>

      <div
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
        role="radiogroup"
        aria-label="Terminal Appearance Themes"
      >
        {availableThemes.map((t) => {
          const isSelected = theme === t.id;
          return (
            <ThemeCard
              key={t.id}
              themeDef={t}
              isSelected={isSelected}
              onSelect={() => setTheme(t.id)}
            />
          );
        })}
      </div>
    </div>
  );
};

interface ThemeCardProps {
  themeDef: ThemeDefinition;
  isSelected: boolean;
  onSelect: () => void;
}

const ThemeCard: React.FC<ThemeCardProps> = ({ themeDef, isSelected, onSelect }) => {
  const { colors } = themeDef;

  return (
    <div
      role="radio"
      aria-checked={isSelected}
      aria-label={themeDef.name}
      tabIndex={0}
      onClick={onSelect}
      onKeyDown={(e) => {
        if (e.key === ' ' || e.key === 'Enter') {
          e.preventDefault();
          onSelect();
        }
      }}
      className={`relative group rounded-xl p-4 transition-all duration-200 cursor-pointer select-none interactive-card outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)] ${
        isSelected
          ? 'bg-[var(--bg-surface-elevated)] border-2 border-[var(--accent-primary)] shadow-lg shadow-[var(--accent-muted)]'
          : 'bg-[var(--bg-surface)] border border-[var(--border-default)] hover:border-[var(--border-strong)] hover:bg-[var(--bg-surface-elevated)]'
      }`}
    >
      {/* Miniature CSS-Only UI Preview */}
      <div
        className="w-full h-24 rounded-lg p-2.5 mb-3 border flex flex-col justify-between overflow-hidden shadow-inner"
        style={{
          backgroundColor: colors.bgApp,
          borderColor: colors.borderDefault,
        }}
      >
        {/* Mini Header Strip */}
        <div
          className="flex items-center justify-between pb-1.5 border-b"
          style={{ borderColor: colors.borderSubtle }}
        >
          <div className="flex items-center gap-1.5">
            <span
              className="w-2 h-2 rounded-full"
              style={{ backgroundColor: colors.accentPrimary }}
            />
            <span
              className="text-[9px] font-mono font-bold tracking-wider"
              style={{ color: colors.textPrimary }}
            >
              TERMINAL
            </span>
          </div>
          <span
            className="text-[8px] font-mono px-1.5 py-0.2 rounded"
            style={{
              backgroundColor: colors.accentMuted,
              color: colors.accentPrimary,
              border: `1px solid ${colors.accentPrimary}40`,
            }}
          >
            {themeDef.type === 'light' ? 'LIGHT' : 'DARK'}
          </span>
        </div>

        {/* Mini Content Row */}
        <div className="grid grid-cols-3 gap-1.5 my-auto">
          {/* Card 1: Metric */}
          <div
            className="rounded p-1 border flex flex-col justify-between"
            style={{
              backgroundColor: colors.bgSurface,
              borderColor: colors.borderDefault,
            }}
          >
            <span
              className="text-[7px] font-mono truncate"
              style={{ color: colors.textMuted }}
            >
              NET P&L
            </span>
            <span
              className="text-[8px] font-mono font-bold truncate"
              style={{ color: colors.accentPrimary }}
            >
              +₹4,850
            </span>
          </div>

          {/* Card 2: Exposure */}
          <div
            className="rounded p-1 border flex flex-col justify-between"
            style={{
              backgroundColor: colors.bgSurface,
              borderColor: colors.borderDefault,
            }}
          >
            <span
              className="text-[7px] font-mono truncate"
              style={{ color: colors.textMuted }}
            >
              EXPOSURE
            </span>
            <span
              className="text-[8px] font-mono font-bold truncate"
              style={{ color: colors.accentSecondary }}
            >
              ₹67.2K
            </span>
          </div>

          {/* Card 3: Mini chart/bars */}
          <div
            className="rounded p-1 border flex items-end justify-between gap-0.5"
            style={{
              backgroundColor: colors.bgSurface,
              borderColor: colors.borderDefault,
            }}
          >
            <div
              className="w-1.5 rounded-t"
              style={{ height: '40%', backgroundColor: colors.borderStrong }}
            />
            <div
              className="w-1.5 rounded-t"
              style={{ height: '70%', backgroundColor: colors.accentSecondary }}
            />
            <div
              className="w-1.5 rounded-t"
              style={{ height: '95%', backgroundColor: colors.accentPrimary }}
            />
          </div>
        </div>

        {/* Mini Footer Strip */}
        <div
          className="flex items-center justify-between pt-1 text-[7px] font-mono"
          style={{ color: colors.textMuted }}
        >
          <span>ORACLE VM</span>
          <span style={{ color: colors.accentPrimary }}>LIVE</span>
        </div>
      </div>

      {/* Theme Meta Info */}
      <div className="space-y-1.5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5">
            <span className="font-mono font-bold text-xs text-[var(--text-primary)]">
              {themeDef.name}
            </span>
            {themeDef.isDefault && (
              <span className="px-1.5 py-0.2 rounded text-[9px] font-mono bg-emerald-950/80 border border-emerald-800/60 text-emerald-400 font-semibold">
                DEFAULT
              </span>
            )}
          </div>

          {/* Selection indicator pill */}
          {isSelected ? (
            <span className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-mono font-bold bg-emerald-600 text-white shadow-sm">
              <Check className="w-3 h-3" />
              <span>ACTIVE</span>
            </span>
          ) : (
            <span className="text-[10px] font-mono text-[var(--text-muted)] group-hover:text-[var(--text-secondary)]">
              Select
            </span>
          )}
        </div>

        <p className="text-[11px] font-mono text-[var(--text-secondary)] line-clamp-2 leading-relaxed">
          {themeDef.description}
        </p>

        {/* Accent Color Palette Dots */}
        <div className="flex items-center justify-between pt-2 border-t border-[var(--border-subtle)] text-[10px] font-mono text-[var(--text-muted)]">
          <div className="flex items-center gap-1.5">
            {themeDef.type === 'light' ? (
              <Sun className="w-3 h-3 text-amber-400" />
            ) : (
              <Moon className="w-3 h-3 text-cyan-400" />
            )}
            <span>{themeDef.tagline}</span>
          </div>

          <div className="flex items-center gap-1">
            <span
              className="w-2.5 h-2.5 rounded-full border border-black/20"
              style={{ backgroundColor: colors.bgApp }}
              title={`App BG: ${colors.bgApp}`}
            />
            <span
              className="w-2.5 h-2.5 rounded-full border border-black/20"
              style={{ backgroundColor: colors.accentPrimary }}
              title={`Primary Accent: ${colors.accentPrimary}`}
            />
            <span
              className="w-2.5 h-2.5 rounded-full border border-black/20"
              style={{ backgroundColor: colors.accentSecondary }}
              title={`Secondary Accent: ${colors.accentSecondary}`}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
